import { call, put, takeLatest } from "redux-saga/effects";
import { ToastError, ToastSuccess } from "components/Global/Toasts/Toasty";
import { createSelector } from "reselect";
import { uploadFile } from "shared-utility/store/utils/fileHelper";
import {
  defaultReturn
  , multipleObjectReturn
  , makePayload
  , generateDefaultActions
  , requestSaga
  , handleCallback
} from "shared-utility/store/utils/helpers";
import {
  downloadFile
} from "shared-utility/store/utils/downloadFile";
import { formatDate } from "helpers/row-logic-helper";
import { DATA_SOURCE_FACADE, DATA_SOURCE_FACADE_MAP } from "./deal.constants";

// ACTIONS

export const DEAL_GET_ALL_COMPANIES = generateDefaultActions("DEAL_GET_ALL_COMPANIES");
export const getAllCompanies = payload => makePayload(DEAL_GET_ALL_COMPANIES.value, payload);
function* getAllCompaniesSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/allCompanies";
  yield requestSaga(DEAL_GET_ALL_COMPANIES, url);
  yield callback();
}

export const DEAL_SHARE_DEAL = generateDefaultActions("DEAL_SHARE_DEAL");
export const shareDeal = payload => makePayload(DEAL_SHARE_DEAL.value, payload);
function* shareDealSaga(payload) {
  const callback = handleCallback(payload);
  delete payload.callback;
  const url = "/sharing/deal";
  yield requestSaga(DEAL_SHARE_DEAL, url, "POST", payload);
  yield callback();
}

export const DEAL_UNSHARE_DEAL = generateDefaultActions("DEAL_UNSHARE_DEAL");
export const deleteDealShare = payload => makePayload(DEAL_UNSHARE_DEAL.value, payload);
function* deleteDealShareSaga(payload) {
  const callback = handleCallback(payload);
  delete payload.callback;
  const url = "/sharing/deal";
  yield requestSaga(DEAL_UNSHARE_DEAL, url, "DELETE", payload);
  yield callback();
}

export const DEAL_DELETE = generateDefaultActions("DEAL_DELETE");
export const deleteDeal = payload => makePayload(DEAL_DELETE.value, payload);
function* deleteDealSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/deal/deleteDeal/${payload.dealId}`;
  yield requestSaga(DEAL_DELETE, url, "DELETE");
  yield callback();
}

export const DEAL_GET_DEAL = generateDefaultActions("DEAL_GET_DEAL");
export const getDealName = payload => makePayload(DEAL_GET_DEAL.value, payload);
function* getDealNameSaga(payload) {
  const url = `/getDealName/${payload.dealId}`;
  yield requestSaga(DEAL_GET_DEAL, url, "GET");
}

export const CLEAR_STORE = generateDefaultActions("CLEAR_STORE");
export const clearStore = payload => makePayload(CLEAR_STORE.value, payload);
function* clearStoreSaga(payload) {
  yield put({ type: CLEAR_STORE.success });
}

export const CLEAR_STORE_SOFT = generateDefaultActions("CLEAR_STORE_SOFT");
export const clearStoreSoft = payload => makePayload(CLEAR_STORE_SOFT.value, payload);
function* clearStoreSoftSaga() {
  yield put({ type: CLEAR_STORE_SOFT.success });
}

export const DEAL_GET = generateDefaultActions("DEAL_GET");
export const dealGet = payload => makePayload(DEAL_GET.value, payload);
function* dealGetSaga(payload) {
  const url = `/deals/${payload.dealId}`;
  yield requestSaga(DEAL_GET, url, "GET");
}

export const DEAL_GET_GROUPED_DEALS = generateDefaultActions("DEAL_GET_GROUPED_DEALS");
export const getGroupedDeals = (dealId) => makePayload(DEAL_GET_GROUPED_DEALS.value, { dealId });
function* getGroupedDealsSaga(payload) {
  const url = `/deals/${payload.dealId}/groupedDeals`;
  yield requestSaga(DEAL_GET_GROUPED_DEALS, url, "GET");
}

export const DEAL_GET_ALERTS = generateDefaultActions("DEAL_GET_ALERTS");
export const dealGetAlerts = payload => makePayload(DEAL_GET_ALERTS.value, payload);
function* dealGetAlertsSaga(payload) {
  const url = "/getAlertsForDeal";
  yield requestSaga(DEAL_GET_ALERTS, url, "POST", payload);
}

export const DEAL_CANCEL_REFINANCE = generateDefaultActions("DEAL_CANCEL_REFINANCE");
export const cancelDealRefinance = payload => makePayload(DEAL_CANCEL_REFINANCE.value, payload);
function* cancelDealRefinanceSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/cancelDealRefinance";
  yield requestSaga(DEAL_CANCEL_REFINANCE, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const DEAL_REVIVE_DEAL = generateDefaultActions("DEAL_REVIVE_DEAL");
export const reviveDeal = payload => makePayload(DEAL_REVIVE_DEAL.value, payload);
function* reviveDealSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/reviveDeal";
  yield requestSaga(DEAL_REVIVE_DEAL, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const DEAL_KILL_DEAL = generateDefaultActions("DEAL_KILL_DEAL");
export const killDeal = payload => makePayload(DEAL_KILL_DEAL.value, payload);
function* killDealSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/killDeal";
  yield requestSaga(DEAL_KILL_DEAL, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const DEAL_CHANGE_STAGE = generateDefaultActions("DEAL_CHANGE_STAGE");
export const changeDealStage = payload => makePayload(DEAL_CHANGE_STAGE.value, payload);
function* changeDealStageSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/changeDealStage";
  yield requestSaga(DEAL_CHANGE_STAGE, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const DEAL_GET_WINNING_QUOTES = generateDefaultActions("DEAL_GET_WINNING_QUOTES");
export const getWinningQuotes = payload => makePayload(DEAL_GET_WINNING_QUOTES.value, payload);
function* getWinningQuotesSaga(payload) {
  const url = `/getWinningQuotes/${payload.dealId}`;
  yield requestSaga(DEAL_GET_WINNING_QUOTES, url, "GET");
}

export const DEAL_CLOSE_DEAL = generateDefaultActions("DEAL_CLOSE_DEAL");
export const closeDeal = payload => makePayload(DEAL_CLOSE_DEAL.value, payload);
function* closeDealSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = `/deal/${payload.dealId}/close`;
  yield requestSaga(DEAL_CHANGE_STAGE, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const DEAL_GET_DEBT_VALUATION_DATA = generateDefaultActions("DEAL_GET_DEBT_VALUATION_DATA");
export const getDebtValuationData = payload => makePayload(DEAL_GET_DEBT_VALUATION_DATA.value, payload);
function* getDebtValuationDataSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/getDealDebtValuationData/${payload.dealId}`;
  yield requestSaga(DEAL_GET_DEBT_VALUATION_DATA, url);
  yield callback();
}

export const DEAL_GET_APP_ACTIVATION = generateDefaultActions("DEAL_GET_APP_ACTIVATION");
export const getAppActivationStatus = payload => makePayload(DEAL_GET_APP_ACTIVATION.value, payload);
function* getAppActivationStatusSaga(payload) {
  yield getDebtValuationActivationStatusSaga(payload);
  yield getLenderReportingActivationStatusSaga(payload);
  yield put({ type: DEAL_GET_APP_ACTIVATION.success });
}

export const DEAL_GET_DEBT_VALUATION_ACTIVATION = generateDefaultActions("DEAL_GET_DEBT_VALUATION_ACTIVATION");
export const getDebtValuationActivationStatus = payload => makePayload(DEAL_GET_DEBT_VALUATION_ACTIVATION.value, payload);
function* getDebtValuationActivationStatusSaga(payload) {
  const url = "/apps/3fa9c4d0-c4c1-4428-ac86-66129387388e/isActivated";
  yield requestSaga(DEAL_GET_DEBT_VALUATION_ACTIVATION, url);
}

export const DEAL_GET_LENDER_REPORTING_ACTIVATION = generateDefaultActions("DEAL_GET_LENDER_REPORTING_ACTIVATION");
export const getLenderReportingActivationStatus = payload => makePayload(DEAL_GET_LENDER_REPORTING_ACTIVATION.value, payload);
function* getLenderReportingActivationStatusSaga(payload) {
  const url = "/apps/83d35f75-f91e-464c-a534-ddfe5c7873a7/isActivated";
  yield requestSaga(DEAL_GET_LENDER_REPORTING_ACTIVATION, url);
}

// START ATTACHMENTS

export const dealGetAttachments = payload => makePayload(DEAL_GET_ATTACHMENTS.value, payload);
export const DEAL_GET_ATTACHMENTS = generateDefaultActions("DEAL_GET_ATTACHMENTS");
function* dealGetAttachmentsSaga(payload) {
  const url = "/getFiles/byModel";
  yield requestSaga(DEAL_GET_ATTACHMENTS, url, "POST", { modelName: "deal", modelId: payload.dealId });
}

export const DEAL_PUT_ATTACHMENT = generateDefaultActions("DEAL_PUT_ATTACHMENT");
export const dealPutAttachment = payload => makePayload(DEAL_PUT_ATTACHMENT.value, payload);
function* dealPutAttachmentSaga(payload) {
  const url = "/files";
  payload = {
    ...payload,
    id: payload.file.id,
    isPrivate: payload.file.isPrivate,
    name: payload.file.name,
    model: "deal",
    modelId: payload.dealId,
    url: "/files",
    name: payload.file.name
  };

  yield requestSaga(DEAL_PUT_ATTACHMENT, url, "PUT", payload);
  yield put(dealGetAttachments({ dealId: payload.dealId }));
}

export const DEAL_POST_ATTACHMENT = generateDefaultActions("DEAL_POST_ATTACHMENT");
export const dealPostAttachment = payload => makePayload(DEAL_POST_ATTACHMENT.value, payload);
function* dealPostAttachmentSaga(payload) {
  const callback = handleCallback(payload);
  payload = {
    ...payload,
    model: "deal",
    modelId: payload.dealId,
    url: "/files"
  };

  yield call(uploadFile, payload);
  yield put(DEAL_POST_ATTACHMENT.createSuccess());
  yield put(dealGetAttachments({ dealId: payload.dealId }));
  yield callback();
}

export const DEAL_DELETE_ATTACHMENT = generateDefaultActions("DEAL_DELETE_ATTACHMENT");
export const dealDeleteAttachment = payload => makePayload(DEAL_DELETE_ATTACHMENT.value, payload);
function* dealDeleteAttachmentSaga(payload) {
  const url = `/files/${payload.fileId}`;
  const callback = handleCallback(payload);

  yield requestSaga(DEAL_DELETE_ATTACHMENT, url, "DELETE", payload);
  yield put(dealGetAttachments({ dealId: payload.dealId }));
  yield callback();
}

// END ATTACHMENTS

// START IMAGES

export const DEAL_POST_IMAGE = generateDefaultActions("DEAL_POST_IMAGE");
export const dealPostImage = payload => makePayload(DEAL_POST_IMAGE.value, payload);
function* dealPostImageSaga(sagaPayload) {
  const payload = {
    file: sagaPayload.file,
    url: `/deals/${sagaPayload.dealId}/coverphoto`
  };
  yield call(uploadFile, payload);
  yield put(DEAL_POST_IMAGE.createSuccess());
  yield put(dealGet({ dealId: sagaPayload.dealId }));
}

export const DEAL_DELETE_IMAGE = generateDefaultActions("DEAL_DELETE_IMAGE");
export const dealDeleteImage = payload => makePayload(DEAL_DELETE_IMAGE.value, payload);
function* dealDeleteImageSaga(payload) {
  yield requestSaga(DEAL_DELETE_IMAGE, `/deals/${payload.dealId}/coverphoto`, "DELETE");
  yield put(dealGet({ dealId: payload.dealId }));
}

// END IMAGES

// START NOTES

export const DEAL_GET_NOTES = generateDefaultActions("DEAL_GET_NOTES");
export const dealGetNotes = payload => makePayload(DEAL_GET_NOTES.value, payload);
function* dealGetNotesSaga(payload) {
  const url = `/deals/notes/${payload.dealId}`;
  yield requestSaga(DEAL_GET_NOTES, url, "GET");
}

export const DEAL_PUT_NOTE = generateDefaultActions("DEAL_PUT_NOTE");
export const dealPutNote = payload => makePayload(DEAL_PUT_NOTE.value, payload);
function* dealPutNoteSaga(payload) {
  const url = `/notes/${payload.note.id}`;
  yield requestSaga(DEAL_PUT_NOTE, url, "PUT", payload);
  yield put(dealGetNotes({ dealId: payload.dealId }));
}

export const DEAL_POST_NOTE = generateDefaultActions("DEAL_POST_NOTE");
export const dealPostNote = payload => makePayload(DEAL_POST_NOTE.value, payload);
function* dealPostNoteSaga(payload) {
  const url = "/deal/addnote";
  const note = {
    model: "deal",
    modelId: payload.dealId,
    note: payload.note,
    pinned: false
  };
  yield requestSaga(DEAL_POST_NOTE, url, "POST", { note });
  yield put(dealGetNotes({ dealId: payload.dealId }));
}

export const DEAL_DELETE_NOTE = generateDefaultActions("DEAL_DELETE_NOTE");
export const dealDeleteNote = payload => makePayload(DEAL_DELETE_NOTE.value, payload);
function* dealDeleteNoteSaga(payload) {
  const url = `/notes/${payload.noteId}`;
  yield requestSaga(DEAL_DELETE_NOTE, url, "DELETE");
  yield put(dealGetNotes({ dealId: payload.dealId }));
}

export const DEAL_CANCEL_MODIFY_LOANS = generateDefaultActions("DEAL_CANCEL_MODIFY_LOANS");
export const cancelModifyLoans = (dealId, callback) => makePayload(DEAL_CANCEL_MODIFY_LOANS.value, { dealId, callback });
function* cancelModifyLoansSaga(payload) {
  const callback = handleCallback(payload);
  yield requestSaga(DEAL_CANCEL_MODIFY_LOANS, `/deal/${payload.dealId}/cancelModifyLoans`, "POST", payload);
  yield callback();
}
// END NOTES

export const DEAL_UPDATE_DETAILS = generateDefaultActions("DEAL_UPDATE_DETAILS");
export const updateDeal = payload => makePayload(DEAL_UPDATE_DETAILS.value, { payload });
function* updateDealSaga(payload) {
  const callback = handleCallback(payload);
  const deal = payload.payload.deal;
  yield requestSaga(DEAL_UPDATE_DETAILS, `/deals/${payload.payload.id}`, "PUT", { deal: payload.payload.deal });
  yield callback();
}

export const DEAL_ADD_SUPPLEMENTAL_LOANS = generateDefaultActions("DEAL_ADD_SUPPLEMENTAL_LOANS");
export const addSupplementalLoans = (dealId, callback) => makePayload(DEAL_ADD_SUPPLEMENTAL_LOANS.value, { dealId, callback });
function* addSupplementalLoansSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/deal/${payload.dealId}/addSupplementalLoans`;
  yield requestSaga(DEAL_ADD_SUPPLEMENTAL_LOANS, url, "POST");
  yield callback();
}

export const DEAL_CANCEL_ADD_SUPPLEMENTAL = generateDefaultActions("DEAL_CANCEL_ADD_SUPPLEMENTAL");
export const cancelAddSupplemental = (dealId, callback) => makePayload(DEAL_CANCEL_ADD_SUPPLEMENTAL.value, { dealId, callback });
function* cancelDealSupplementalSaga(payload) {
  const callback = handleCallback(payload);
  yield requestSaga(DEAL_CANCEL_ADD_SUPPLEMENTAL, "/deal/cancelAddSupplemental", "POST", payload);
  yield callback();
}

export const DEAL_DOWNLOAD_LOAN_DETAILS = generateDefaultActions("DEAL_DOWNLOAD_LOAN_DETAILS");
export const downloadLoans = payload => makePayload(DEAL_DOWNLOAD_LOAN_DETAILS.value, payload);
function* downloadLoansSaga(payload) {
  /*
   * try {
   * yield put(DEAL_DOWNLOAD_LOAN_DETAILS.setIsDownloading({ isDownloading: true }));
   * yield put(GET_DASHBOARD_PDF_DEAL_SUMMARY.createSuccess(payload));
   */
  yield call(downloadFile, "downloadLoanSummaries", "POST", payload, 600000);
  /*
   * } catch (error) {
   *   yield put(GET_DASHBOARD_PDF_DEAL_SUMMARY.createFailure(error));
   * } finally {
   *   yield put(DASHBOARD_IS_DOWNLOADING.setIsDownloading({ isDownloading: false }));
   * }
   */
}

// REDUCER

const initialState = {
  deal: null,
  groupedDeals: [],
  notes: [],
  alerts: [],
  attachments: [],
  allCompanies: [],
  appActivation: false,
  isDVActive: false,
  isLenderReportingActive: false
};

export function newDealReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case DEAL_SHARE_DEAL.success:
      ToastSuccess("Share in progress");

      return state;

    case DEAL_UNSHARE_DEAL.success:
      ToastSuccess("Deal Sharing Removed");

      return state;

    case DEAL_GET_ALL_COMPANIES.success:
      return defaultReturn(state, payload.companies, "allCompanies");

    case DEAL_DELETE.success:
      ToastSuccess("Successfully Deleted");

      return defaultReturn(state);

    case CLEAR_STORE.success:
      return initialState;
    // DEAL

    case CLEAR_STORE_SOFT.success:
      const deal = state.deal ? defaultReturn(state.deal, null, "dataSourceAsOfDate") : null;
      return multipleObjectReturn(
        initialState,
        ["deal", "guestPermission", "groupedDeals"],
        [deal, state.guestPermission, state.groupedDeals]
      );

    case DEAL_GET_DEAL.success:
      return defaultReturn(state, payload.data, "dealName");

    case DEAL_GET.error:
      ToastError("Data Fetch Unsuccessful");

      return defaultReturn(state);

    case DEAL_GET.success:
      return multipleObjectReturn(state, ["deal", "guestPermission"], [payload.deal, payload.guestPermission]);

    case DEAL_GET_GROUPED_DEALS.success:
      return defaultReturn(state, payload.data, "groupedDeals");

    // ALERTS

    case DEAL_GET_ALERTS.success:
      return defaultReturn(state, payload.result, "alerts");

    // ATTACHMENTS

    case DEAL_GET_ATTACHMENTS.success:
      return defaultReturn(state, payload.data, "attachments");

    case DEAL_PUT_ATTACHMENT.success:
      ToastSuccess("File Successfully Updated");

      return defaultReturn(state);

    case DEAL_POST_ATTACHMENT.success:
      ToastSuccess("File Successfully Added");

      return defaultReturn(state);

    case DEAL_DELETE_ATTACHMENT.success:
      ToastSuccess("File Successfully Deleted");

      return state;

    // NOTES

    case DEAL_GET_NOTES.success:
      return defaultReturn(state, payload.data, "notes");

    case DEAL_POST_NOTE.success:
      ToastSuccess("Note Successfully Added");

      return defaultReturn(state);

    case DEAL_PUT_NOTE.success:
      ToastSuccess("Note Successfully Updated");

      return defaultReturn(state);

    case DEAL_DELETE_NOTE.success:
      ToastSuccess("Note Successfully Deleted");

      return state;

    case DEAL_CANCEL_REFINANCE.success:
      return { ...state };

    case DEAL_GET_WINNING_QUOTES.success:
      return defaultReturn(state, payload.data, "winningQuotes");

    case DEAL_GET_DEBT_VALUATION_DATA.success:
      return defaultReturn(state, payload, "dvData");

    case DEAL_GET_APP_ACTIVATION.success:
      return defaultReturn(state, true, "appActivation");

    case DEAL_GET_DEBT_VALUATION_ACTIVATION.success:
      return defaultReturn(state, payload.data, "isDVActive");

    case DEAL_GET_LENDER_REPORTING_ACTIVATION.success:
      return defaultReturn(state, payload.data, "isLenderReportingActive");

    case DEAL_CLOSE_DEAL.success:
      ToastSuccess("Deal Successfully Closed");

      return state;

    case DEAL_CANCEL_MODIFY_LOANS.success:
      ToastSuccess("Deal Modification Canceled");

      return state;

    case DEAL_UPDATE_DETAILS.success:
      ToastSuccess("Deal Update Complete");

      return state;
    case DEAL_UPDATE_DETAILS.failure:
      ToastSuccess("Deal Update Failed");

      return state;

    case DEAL_DOWNLOAD_LOAN_DETAILS:
      ToastSuccess("Download in progress");

      return state;
    // DEFAULT
    default:
      return state;
  }
}

// SAGA

export function* newDealSaga() {
  yield takeLatest(DEAL_GET.value, dealGetSaga);
  yield takeLatest(DEAL_GET_GROUPED_DEALS.value, getGroupedDealsSaga);
  yield takeLatest(DEAL_GET_ALERTS.value, dealGetAlertsSaga);
  yield takeLatest(DEAL_GET_ATTACHMENTS.value, dealGetAttachmentsSaga);
  yield takeLatest(DEAL_PUT_ATTACHMENT.value, dealPutAttachmentSaga);
  yield takeLatest(DEAL_POST_ATTACHMENT.value, dealPostAttachmentSaga);
  yield takeLatest(DEAL_DELETE_ATTACHMENT.value, dealDeleteAttachmentSaga);
  yield takeLatest(DEAL_POST_IMAGE.value, dealPostImageSaga);
  yield takeLatest(DEAL_DELETE_IMAGE.value, dealDeleteImageSaga);
  yield takeLatest(DEAL_GET_NOTES.value, dealGetNotesSaga);
  yield takeLatest(DEAL_PUT_NOTE.value, dealPutNoteSaga);
  yield takeLatest(DEAL_POST_NOTE.value, dealPostNoteSaga);
  yield takeLatest(DEAL_DELETE_NOTE.value, dealDeleteNoteSaga);
  yield takeLatest(DEAL_CANCEL_REFINANCE.value, cancelDealRefinanceSaga);
  yield takeLatest(DEAL_REVIVE_DEAL.value, reviveDealSaga);
  yield takeLatest(DEAL_KILL_DEAL.value, killDealSaga);
  yield takeLatest(DEAL_CHANGE_STAGE.value, changeDealStageSaga);
  yield takeLatest(DEAL_GET_WINNING_QUOTES.value, getWinningQuotesSaga);
  yield takeLatest(DEAL_CLOSE_DEAL.value, closeDealSaga);
  yield takeLatest(DEAL_CANCEL_MODIFY_LOANS.value, cancelModifyLoansSaga);
  yield takeLatest(DEAL_UPDATE_DETAILS.value, updateDealSaga);
  yield takeLatest(DEAL_ADD_SUPPLEMENTAL_LOANS.value, addSupplementalLoansSaga);
  yield takeLatest(DEAL_CANCEL_ADD_SUPPLEMENTAL.value, cancelDealSupplementalSaga);
  yield takeLatest(CLEAR_STORE.value, clearStoreSaga);
  yield takeLatest(CLEAR_STORE_SOFT.value, clearStoreSoftSaga);
  yield takeLatest(DEAL_GET_DEAL.value, getDealNameSaga);
  yield takeLatest(DEAL_DELETE.value, deleteDealSaga);
  yield takeLatest(DEAL_DOWNLOAD_LOAN_DETAILS.value, downloadLoansSaga);
  yield takeLatest(DEAL_GET_ALL_COMPANIES.value, getAllCompaniesSaga);
  yield takeLatest(DEAL_SHARE_DEAL.value, shareDealSaga);
  yield takeLatest(DEAL_UNSHARE_DEAL.value, deleteDealShareSaga);
  yield takeLatest(DEAL_GET_DEBT_VALUATION_DATA.value, getDebtValuationDataSaga);
  yield takeLatest(DEAL_GET_APP_ACTIVATION.value, getAppActivationStatusSaga);
  yield takeLatest(DEAL_GET_DEBT_VALUATION_ACTIVATION.value, getDebtValuationActivationStatusSaga);
  yield takeLatest(DEAL_GET_LENDER_REPORTING_ACTIVATION.value, getLenderReportingActivationStatusSaga);
}

// SELECTORS

export const mapDataSourceAsOfDateSelector = createSelector(
  (newDeal) => newDeal.deal?.dataSourceAsOfDate,
  (value) => formatDate(value)
);
export const mapServicedDealSelector = createSelector(
  (newDeal) => newDeal.groupedDeals,
  (deals) =>
    deals.find(
      (d) =>
        DATA_SOURCE_FACADE_MAP[d.dataSource] === DATA_SOURCE_FACADE.SERVICED
    )
);
export const mapPublicDealSelector = createSelector(
  (newDeal) => newDeal.groupedDeals,
  (deals) =>
    deals.find(
      (d) =>
        DATA_SOURCE_FACADE_MAP[d.dataSource] === DATA_SOURCE_FACADE.PUBLIC
    )
);
export const mapSreoDealSelector = createSelector(
  (newDeal) => newDeal.groupedDeals,
  (deals) =>
    deals.find(
      (d) =>
        DATA_SOURCE_FACADE_MAP[d.dataSource] === DATA_SOURCE_FACADE.SREO
    )
);